/**
 * Mijn Gegevens — klant profile view/edit card + Contact opnemen mailto.
 * Pure presentational; all state lives in the parent ClientDashboard.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { User, Edit, Check, X as XIcon, AlertTriangle, MapPin, Phone, Mail } from 'lucide-react';

const MyProfileCard = ({
  profile,
  editingProfile,
  setEditingProfile,
  profileForm,
  setProfileForm,
  savingProfile,
  handleSaveProfile,
  i18n,
}) => {
  const reset = () => setProfileForm({
    name: profile?.name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    company_name: profile?.company_name || '',
  });

  return (
    <div className="mb-8">
      <Card className="bg-[#0d3d3d]/90 border-white/20" data-testid="client-profile-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <User className="w-5 h-5 text-[#3da5a5]" /> {i18n.language === 'nl' ? 'Mijn Gegevens' : 'My Details'}
          </CardTitle>
          {!editingProfile ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingProfile(true)}
              className="border-white/30 text-white hover:bg-white/10"
              data-testid="edit-profile-btn"
            >
              <Edit className="w-3 h-3 mr-1" /> {i18n.language === 'nl' ? 'Bewerken' : 'Edit'}
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setEditingProfile(false); reset(); }}
                className="border-white/30 text-white hover:bg-white/10"
                disabled={savingProfile}
                data-testid="cancel-profile-btn"
              >
                <XIcon className="w-3 h-3 mr-1" /> {i18n.language === 'nl' ? 'Annuleren' : 'Cancel'}
              </Button>
              <Button
                size="sm"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="bg-[#3da5a5] hover:bg-[#2d8585]"
                data-testid="save-profile-btn"
              >
                <Check className="w-3 h-3 mr-1" />
                {savingProfile
                  ? (i18n.language === 'nl' ? 'Opslaan...' : 'Saving...')
                  : (i18n.language === 'nl' ? 'Opslaan' : 'Save')}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-2">
          {!editingProfile ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-white">
              <Field testid="profile-name" label={i18n.language === 'nl' ? 'Naam' : 'Name'} icon={<User className="w-3.5 h-3.5 text-[#3da5a5]" />} value={profile?.name} />
              <Field testid="profile-email" label="E-mail" icon={<Mail className="w-3.5 h-3.5 text-[#3da5a5]" />} value={profile?.email} />
              <Field testid="profile-phone" label={i18n.language === 'nl' ? 'Telefoon' : 'Phone'} icon={<Phone className="w-3.5 h-3.5 text-[#3da5a5]" />} value={profile?.phone} />
              <Field testid="profile-address" label={i18n.language === 'nl' ? 'Adres' : 'Address'} icon={<MapPin className="w-3.5 h-3.5 text-[#3da5a5]" />} value={profile?.address} />
              {profile?.company_name && (
                <div className="md:col-span-2" data-testid="profile-company">
                  <p className="text-[10px] uppercase tracking-wider text-white/50 mb-0.5">{i18n.language === 'nl' ? 'Bedrijf' : 'Company'}</p>
                  <p className="text-sm">{profile.company_name}</p>
                </div>
              )}
              <MismatchWarning profile={profile} i18n={i18n} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <EditInput value={profileForm.name} onChange={(v) => setProfileForm({ ...profileForm, name: v })} label={i18n.language === 'nl' ? 'Naam' : 'Name'} testid="profile-name-input" />
              <EditInput type="email" value={profileForm.email} onChange={(v) => setProfileForm({ ...profileForm, email: v })} label="E-mail" testid="profile-email-input" />
              <EditInput value={profileForm.phone} onChange={(v) => setProfileForm({ ...profileForm, phone: v })} label={i18n.language === 'nl' ? 'Telefoon' : 'Phone'} testid="profile-phone-input" />
              <EditInput value={profileForm.address} onChange={(v) => setProfileForm({ ...profileForm, address: v })} label={i18n.language === 'nl' ? 'Adres' : 'Address'} testid="profile-address-input" />
            </div>
          )}

          {!editingProfile && profile && <ContactCTA profile={profile} i18n={i18n} />}
        </CardContent>
      </Card>
    </div>
  );
};

const Field = ({ testid, label, icon, value }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-white/50 mb-0.5">{label}</p>
    <p className="text-sm flex items-center gap-2">{icon}{value || '—'}</p>
  </div>
);

const EditInput = ({ label, value, onChange, testid, type = 'text' }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-white/50">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/10 border border-white/20 rounded p-2 text-white text-sm focus:outline-none focus:border-[#3da5a5]"
      data-testid={testid}
    />
  </div>
);

const MismatchWarning = ({ profile, i18n }) => {
  const c = profile?.canonical_in_crm;
  if (!c) return null;
  const addrMismatch = (c.address || '').trim().toLowerCase() !== (profile?.address || '').trim().toLowerCase();
  const emailMismatch = (c.email || '').trim().toLowerCase() !== (profile?.email || '').trim().toLowerCase();
  if (!addrMismatch && !emailMismatch) return null;
  return (
    <div className="md:col-span-2 mt-2 p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg" data-testid="profile-mismatch-warning">
      <p className="text-xs font-semibold text-amber-300 flex items-center gap-1 mb-1">
        <AlertTriangle className="w-3 h-3" />
        {i18n.language === 'nl' ? 'Let op: uw gegevens wijken af van wat Cardoso Bouw bij ons heeft staan' : 'Note: your data differs from what Cardoso Bouw has on file'}
      </p>
      <p className="text-[11px] text-amber-200/80">{i18n.language === 'nl' ? 'Bij ons geregistreerd:' : 'On file:'} <strong>{c.label}</strong> — {c.address} ({c.email})</p>
      <p className="text-[11px] text-amber-200/80 mt-1">{i18n.language === 'nl' ? 'Klik op "Bewerken" hierboven om uw gegevens bij te werken, of neem contact op.' : 'Click "Edit" above to update your data, or contact us.'}</p>
    </div>
  );
};

const ContactCTA = ({ profile, i18n }) => {
  const body = `Hallo Cardoso Bouw,\n\nMijn gegevens in het portaal zijn:\n• Naam: ${profile.name || '-'}\n• E-mail: ${profile.email || '-'}\n• Telefoon: ${profile.phone || '-'}\n• Adres: ${profile.address || '-'}\n${profile.company_name ? `• Bedrijf: ${profile.company_name}\n` : ''}\nGraag de volgende aanpassing doorvoeren:\n[hier uw opmerking]\n\nMet vriendelijke groet,\n${profile.name || ''}`;
  const subject = `Mijn gegevens — ${profile.name || profile.email || 'Klant'}`;
  return (
    <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <p className="text-xs text-white/60">
        {i18n.language === 'nl'
          ? 'Iets klopt niet? Stuur ons een berichtje, wij passen het meteen aan.'
          : 'Something not right? Send us a message and we will fix it right away.'}
      </p>
      <a
        href={`mailto:info@cardosobouw.nl?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
        className="inline-flex items-center justify-center gap-2 bg-[#3da5a5] hover:bg-[#2d8585] text-white text-sm font-medium px-4 py-2 rounded transition-colors no-underline"
        data-testid="contact-opnemen-btn"
      >
        <Mail className="w-4 h-4" />
        {i18n.language === 'nl' ? 'Contact opnemen' : 'Contact us'}
      </a>
    </div>
  );
};

export default MyProfileCard;
